import React from 'react';
import { Check, X } from 'lucide-react';

export interface PasswordRequirement {
    label: string;
    regex: RegExp;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
    { label: 'At least 8 characters', regex: /.{8,}/ },
    { label: 'One uppercase letter', regex: /[A-Z]/ },
    { label: 'One lowercase letter', regex: /[a-z]/ },
    { label: 'One number', regex: /[0-9]/ },
    { label: 'One special character (!@#$%^&*)', regex: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/ },
];

export const isPasswordStrong = (password: string): boolean => {
    return PASSWORD_REQUIREMENTS.every((req) => req.regex.test(password));
};

export const getPasswordStrength = (password: string): { level: number; label: string; color: string } => {
    if (!password) return { level: 0, label: '', color: '' };

    const passed = PASSWORD_REQUIREMENTS.filter((req) => req.regex.test(password)).length;
    const total = PASSWORD_REQUIREMENTS.length;
    const ratio = passed / total;

    if (ratio <= 0.2) return { level: 1, label: 'Very Weak', color: '#EF4444' };
    if (ratio <= 0.4) return { level: 2, label: 'Weak', color: '#F97316' };
    if (ratio <= 0.6) return { level: 3, label: 'Fair', color: '#EAB308' };
    if (ratio <= 0.8) return { level: 4, label: 'Good', color: '#22C55E' };
    return { level: 5, label: 'Strong', color: '#16A34A' };
};

interface PasswordStrengthCheckerProps {
    password: string;
}

const PasswordStrengthChecker: React.FC<PasswordStrengthCheckerProps> = ({ password }) => {
    if (!password) return null;

    const strength = getPasswordStrength(password);

    return (
        <div className="mt-3 space-y-3">
            {/* Strength Bar */}
            <div>
                <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500">Password strength</span>
                    <span className="text-xs font-semibold" style={{ color: strength.color }}>
                        {strength.label}
                    </span>
                </div>
                <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div
                            key={i}
                            className="h-1.5 flex-1 rounded-full transition-all duration-300"
                            style={{
                                backgroundColor: i <= strength.level ? strength.color : '#E5E7EB',
                            }}
                        />
                    ))}
                </div>
            </div>

            {/* Requirements Checklist */}
            <div className="space-y-1.5">
                {PASSWORD_REQUIREMENTS.map((req, index) => {
                    const passed = req.regex.test(password);
                    return (
                        <div key={index} className="flex items-center gap-2">
                            {passed ? (
                                <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
                            ) : (
                                <X className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                            )}
                            <span
                                className={`text-xs transition-colors ${passed ? 'text-green-600' : 'text-gray-400'
                                    }`}
                            >
                                {req.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PasswordStrengthChecker;
